import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import m from 'mithril'

type Point = { lat: number; lon: number }

type Attrs = {
  /** Where the pin sits; null shows the map with no pin until the user clicks. */
  pin: Point | null
  /** Where to look when there is no pin yet. */
  fallback?: Point | null
  /** Omit for a display-only map: no dragging, and taps don't move the pin. */
  onmove?: (point: Point) => void
}

const PIN_ZOOM = 17

// A div icon sidesteps Leaflet's default marker images, which bundlers fail to resolve.
const icon = L.divIcon({ className: 'pin-marker', iconSize: [22, 22], iconAnchor: [11, 22] })

/** A map with one pin. With `onmove` the pin is draggable and clicking the map moves it there. */
export const PinMap: m.ClosureComponent<Attrs> = () => {
  let map: L.Map | null = null
  let marker: L.Marker | null = null
  let resizes: ResizeObserver | null = null
  let onmove: Attrs['onmove']

  const place = (point: Point, pan: boolean) => {
    if (!map) return
    const at = L.latLng(point.lat, point.lon)
    if (marker) marker.setLatLng(at)
    else {
      marker = L.marker(at, { icon, draggable: onmove != null }).addTo(map)
      marker.on('dragend', () => {
        const { lat, lng } = marker!.getLatLng()
        onmove?.({ lat, lon: lng })
      })
    }
    // getZoom() is undefined until the map has had a first view.
    if (pan) map.setView(at, Math.max(map.getZoom() || 0, PIN_ZOOM))
  }

  const sameSpot = (point: Point) => {
    const current = marker?.getLatLng()
    return current != null && current.lat === point.lat && current.lng === point.lon
  }

  return {
    oncreate: ({ dom, attrs }) => {
      onmove = attrs.onmove
      map = L.map(dom as HTMLElement)
      L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      }).addTo(map)
      map.on('click', (event: L.LeafletMouseEvent) => {
        if (!onmove) return
        const point = { lat: event.latlng.lat, lon: event.latlng.lng }
        place(point, false)
        onmove(point)
      })

      if (attrs.pin) place(attrs.pin, true)
      else if (attrs.fallback) map.setView([attrs.fallback.lat, attrs.fallback.lon], 14)
      else map.setView([20, 0], 2)

      // Leaflet measures its box once; re-measure when layout settles or changes, or tiles and
      // the pin land in the wrong place.
      resizes = new ResizeObserver(() => {
        map?.invalidateSize()
        if (marker) map?.panTo(marker.getLatLng(), { animate: false })
      })
      resizes.observe(dom)
    },
    // Only an outside change (a fresh address lookup) moves the pin; drags already put it there.
    onupdate: ({ attrs }) => {
      onmove = attrs.onmove
      if (attrs.pin && !sameSpot(attrs.pin)) place(attrs.pin, true)
    },
    onremove: () => {
      resizes?.disconnect()
      map?.remove()
      map = null
      marker = null
    },
    view: () => m('div.pin-map'),
  }
}
